import {
	CheckMark,
	Star,
	StarEmpty,
	Times,
	TrashFull,
	Stale,
	ChatGPTUnfilled
} from '@/resources/assets/images';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import type { RootState } from '@/rootReducer';
import type { EmailTemplate } from '@/modules/email/emailTypes';

type Props = {
	onSelect?: () => void;
	onCancel?: () => void;
	onSave?: ({ name }: { name: string }) => void;
	onDelete?: () => void;
	onDuplicate?: () => void;
	onPin?: () => void;
	isEditable?: boolean;
	name?: string;
	pined?: boolean;
	defaultTemplate?: boolean;
	template: EmailTemplate;
	isAISnippet?: boolean;
};

export default function EmailTemplateListItem({
	onSelect,
	isEditable,
	name,
	pined,
	onCancel,
	onSave,
	onDelete,
	onDuplicate,
	onPin,
	defaultTemplate,
	template,
	isAISnippet
}: Props): JSX.Element {
	const [staleTemplates] = useSelector<RootState>(
		(state: RootState) => [
			state.email.getIn(['templates', 'stale']).toJS()
		],
		shallowEqual
	);
	const [templateName, setTemplateName] = useState<string>('');
	const [showEdit, setShowEdit] = useState<boolean>();

	useEffect(() => {
		if (showEdit) {
			setTemplateName(name as string);
		}
	}, [showEdit]);

	return (
		<div
			id="email-template-list"
			className="emailTemplateList-item flex items-center justify-between transition duration-300 ease-in-out"
		>
			<button
				className="flex-1 p-5 py-4 text-left text-white"
				type="button"
				onClick={!isEditable && !showEdit ? onSelect : undefined}
			>
				{(isEditable || showEdit) && (
					<input
						type="text"
						className="h-auto w-2/5 bg-white py-1.5 text-black"
						placeholder="Name your template"
						value={templateName}
						ref={el => el?.focus()}
						onChange={e => setTemplateName(e.target.value)}
						onKeyDown={e => {
							if (e.code.toLowerCase() === 'enter' && onSave) {
								onSave({ name: templateName });
							}
						}}
					/>
				)}
				{!isEditable && !showEdit && (
					<span className="emailTemplateList-item--name block flex items-center py-1.5">
						<span
							className={`emailTemplateList-item--name-text ${
								template.isStale ||
								staleTemplates?.[`${template.id}`]
									? 'stale'
									: ''
							} `}
						>
							{name}
						</span>
						{isAISnippet && (
							<div className="emailTemplateList-item-stale">
								<ChatGPTUnfilled className="emailTemplateList-item-stale-icon" />
								<span className="emailTemplateList-item-stale-hovertext smaller-tooltip">
									This template contains an AI snippet
								</span>
							</div>
						)}
						{(template.isStale ||
							staleTemplates?.[`${template.id}`]) && (
							<span className="emailTemplateList-item-stale">
								<Stale className="emailTemplateList-item-stale-icon" />
								<span className="emailTemplateList-item-stale-text">
									Stale
								</span>
								<span className="emailTemplateList-item-stale-hovertext">
									If an email template has been used more than
									500 times, it will be marked as "stale".
									<br />
									<br />
									In order to prevent spam, you are not
									allowed to send or schedule an email using a
									stale template. Please change
									<span>
										{' '}
										at least 20% of the body of your email,
										and at least 50% of the words in the
										subject.
									</span>
								</span>
							</span>
						)}
					</span>
				)}
			</button>
			{(isEditable || showEdit) && (
				<div className="mr-2 flex items-center">
					<button
						type="button"
						className="mx-3 inline-flex items-center whitespace-nowrap text-base text-orange"
						onClick={() => {
							if (onCancel) {
								onCancel();
							}

							setShowEdit(false);
						}}
					>
						<Times className="mr-2" width={12} height={12} />
						Cancel
					</button>
					<button
						type="button"
						className={classNames(
							'mx-3 inline-flex items-center whitespace-nowrap text-base text-orange',
							{
								'passive cursor-not-allowed':
									templateName?.length === 0
							}
						)}
						onClick={() => onSave && onSave({ name: templateName })}
						disabled={templateName?.length === 0}
					>
						<CheckMark className="mr-2" />
						Save
					</button>
				</div>
			)}
			{!isEditable && !showEdit && (
				<div className="emailTemplateList-controls mr-2 items-center">
					{!defaultTemplate ? (
						<button
							onClick={onDelete}
							type="button"
							className="mx-3 inline-flex items-center whitespace-nowrap text-base text-orange"
						>
							<TrashFull className="mr-2" /> Delete
						</button>
					) : (
						''
					)}
					{!template.isStale &&
						!staleTemplates?.[`${template.id}`] && (
							<button
								onClick={onDuplicate}
								type="button"
								className="mx-3 inline-flex items-center whitespace-nowrap text-base text-orange"
							>
								Duplicate
							</button>
						)}
					{!defaultTemplate &&
					!template.isStale &&
					!staleTemplates?.[`${template.id}`] ? (
						<button
							onClick={onPin}
							type="button"
							className={`mx-3 inline-flex items-center whitespace-nowrap text-sm text-orange text-base${
								pined ? 'emailTemplateList-control-active' : ''
							}`}
						>
							{pined && <Star className="mr-2 text-orange" />}
							{!pined && (
								<StarEmpty className="mr-2 text-orange" />
							)}
							{!pined && 'Pin to top'}
						</button>
					) : (
						''
					)}
				</div>
			)}
		</div>
	);
}
